import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const S = {
  page: { padding: "0 0 80px 0", fontFamily: "system-ui, sans-serif", background: "#f5f5f5", minHeight: "100vh" },
  header: { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { margin: 0, fontSize: "1.1rem", fontWeight: 700 },
  back: { color: "#fff", textDecoration: "none", fontSize: "0.85rem", opacity: 0.9 },
  card: { margin: "0.75rem 1rem", background: "#fff", borderRadius: 12, padding: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  h2: { margin: "0 0 0.75rem", fontSize: "0.95rem", color: "#374151" },
  row: { display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" },
  label: { display: "block", fontSize: "0.7rem", color: "#6b7280", marginBottom: 4, textTransform: "uppercase" },
  input: { padding: "0.5rem 0.7rem", borderRadius: 8, border: "1px solid #d1d5db", fontSize: "0.9rem", width: 140 },
  btn: (bg) => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "0.55rem 1.1rem", fontWeight: 700, cursor: "pointer" }),
  bar: { height: 10, background: "#e5e7eb", borderRadius: 6, overflow: "hidden", margin: "0.5rem 0" },
  th: { textAlign: "left", fontSize: "0.68rem", color: "#6b7280", textTransform: "uppercase", padding: "0.4rem 0.5rem", borderBottom: "1px solid #f3f4f6" },
  td: { fontSize: "0.82rem", padding: "0.45rem 0.5rem", borderBottom: "1px solid #f9fafb", color: "#374151" },
};

export default function BonusManagement() {
  const [data, setData] = useState(null);
  const [pool, setPool] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${localStorage.getItem("adminToken")}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/bonus`, { headers });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  const act = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const create = () =>
    act(async () => {
      await axios.post(`${API_URL}/admin/bonus`, { total_pool: Number(pool), amount_per_claim: Number(amount) }, { headers });
      setPool("");
      setAmount("");
    });

  const close = () => {
    if (!window.confirm("Close the active bonus now? Unclaimed money will not be paid out.")) return;
    act(() => axios.post(`${API_URL}/admin/bonus/close`, {}, { headers }));
  };

  const active = data?.active;
  const preview = Number(pool) > 0 && Number(amount) > 0 ? Math.floor(Number(pool) / Number(amount)) : 0;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>🎁 Bonus</h1>
        <Link to="/dashboard" style={S.back}>← Back</Link>
      </header>

      {error && <div style={{ ...S.card, color: "#b91c1c" }}>{error}</div>}

      <div style={S.card}>
        <h2 style={S.h2}>Active bonus</h2>
        {active ? (
          <>
            <div><strong>{active.amount_per_claim} Birr</strong> per player · pool {active.total_pool} Birr</div>
            <div style={S.bar}>
              <div style={{ height: "100%", width: `${(active.claimed_count / active.max_claims) * 100}%`, background: "#22c55e" }} />
            </div>
            <div style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}>
              Claimed {active.claimed_count} / {active.max_claims} · {active.remaining_claims} left
            </div>
            <button style={S.btn("#ef4444")} onClick={close} disabled={busy}>Close bonus</button>
          </>
        ) : (
          <div style={{ color: "#6b7280", fontSize: "0.85rem" }}>No active bonus. Players do not see a bonus button.</div>
        )}
      </div>

      {!active && (
        <div style={S.card}>
          <h2 style={S.h2}>Start a new bonus</h2>
          <div style={S.row}>
            <div>
              <label style={S.label}>Total pool (Birr)</label>
              <input style={S.input} type="number" min="1" value={pool} onChange={(e) => setPool(e.target.value)} placeholder="1000" />
            </div>
            <div>
              <label style={S.label}>Per player (Birr)</label>
              <input style={S.input} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50" />
            </div>
            <button style={S.btn("#16a34a")} onClick={create} disabled={busy || preview < 1}>Start bonus</button>
          </div>
          <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.6rem" }}>
            {preview > 0 ? `First ${preview} players can claim. ` : ""}
            Players need at least one deposit, and can claim at most {data?.max_claims_per_player ?? 10} bonuses ever. Withdrawal rules (minimum games played) still apply.
          </div>
        </div>
      )}

      <div style={S.card}>
        <h2 style={S.h2}>History</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={S.th}>#</th><th style={S.th}>Per player</th><th style={S.th}>Pool</th><th style={S.th}>Claimed</th><th style={S.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.history || []).map((c) => (
              <tr key={c.id}>
                <td style={S.td}>{c.id}</td>
                <td style={S.td}>{c.amount_per_claim}</td>
                <td style={S.td}>{c.total_pool}</td>
                <td style={S.td}>{c.claimed_count}/{c.max_claims}</td>
                <td style={S.td}>{c.is_active ? "Active" : "Closed"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
